import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 0;
  background: var(--gradient-surface);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 1200px;
    height: 1200px;
    border-radius: 50%;
    background: var(--gradient-primary);
    top: -600px;
    right: -300px;
    opacity: 0.05;
    animation: float 8s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-30px) rotate(5deg); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Content = styled.div`
  order: 2;
  animation: slideIn var(--transition-normal) forwards;
  
  h2 {
    font-size: 42px;
    font-weight: 800;
    margin-bottom: 24px;
    line-height: 1.2;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 18px;
    color: var(--text-secondary);
    margin-bottom: 20px;
    line-height: 1.6;
  }
`;

const IPadFrame = styled.div`
  background: var(--surface);
  border-radius: 40px;
  padding: 20px;
  box-shadow: var(--shadow-lg);
  width: 700px;
  max-width: 100%;
  order: 1;
  position: relative;
  transition: all var(--transition-normal);
  border: 1px solid var(--border);
  animation: fadeIn var(--transition-normal) forwards;

  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-primary);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 4px 4px 0 0;
  }

  @media (max-width: 968px) {
    margin: 40px auto;
  }
`;

const Screen = styled.div`
  background: var(--background);
  border-radius: 30px;
  overflow: hidden;
  height: 500px;
  padding: 24px;
  transition: all var(--transition-normal);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 12px;
  background: var(--surface);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);

  h2 {
    font-size: 24px;
    font-weight: 600;
    color: var(--text-primary);
  }
`;

const NewButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-primary);
  }

  &::before {
    content: '+';
    font-size: 18px;
  }
`;

const SearchBar = styled.div`
  margin-bottom: 24px;
  position: relative;

  input {
    width: 100%;
    padding: 12px 16px;
    padding-left: 44px;
    border: 1px solid var(--border);
    border-radius: 12px;
    font-size: 14px;
    outline: none;
    background: var(--surface);
    color: var(--text-primary);
    transition: all var(--transition-normal);

    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px var(--primary-light);
    }

    &::placeholder {
      color: var(--text-secondary);
    }
  }

  &::before {
    content: "🔍";
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-secondary);
    font-size: 14px;
  }
`;

const Table = styled.div`
  width: 100%;
  background: var(--surface);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px;
  padding: 16px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  align-items: center;
  font-size: 14px;
  color: var(--text-primary);
  transition: all var(--transition-normal);

  &:hover {
    background: var(--surface-hover);
    transform: translateX(4px);
  }

  span:first-child {
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '🏷️';
      font-size: 16px;
    }
  }

  .actions {
    display: flex;
    gap: 12px;
    
    button {
      border: none;
      background: none;
      font-size: 13px;
      padding: 6px 12px;
      border-radius: 6px;
      transition: all var(--transition-normal);
      font-weight: 500;
      
      &.edit {
        color: var(--primary);
        &:hover {
          background: var(--primary-light);
          color: var(--primary-dark);
        }
      }
      
      &.delete {
        color: var(--error);
        &:hover {
          background: #FEE2E2;
          color: #DC2626;
        }
      }
    }
  }
`;

const Footer = styled.div`
  padding: 16px;
  color: var(--text-secondary);
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--surface);
  border-top: 1px solid var(--border);
  
  select {
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid var(--border);
    background: var(--background);
    color: var(--text-primary);
    font-size: 13px;
    transition: all var(--transition-normal);

    &:focus {
      border-color: var(--primary);
      outline: none;
    }
  }
`;

const TagsSection = () => {
  const tags = [
    'Development',
    'Entertainment',
    'Utilities',
    'Tools & Software'
  ];

  return (
    <Section>
      <Container>
        <IPadFrame>
          <Screen>
            <Header>
              <h2>Tags</h2>
              <NewButton>New tag</NewButton>
            </Header>

            <SearchBar>
              <input type="text" placeholder="Search tags..." />
            </SearchBar>

            <Table>
              <TableHeader>
                <span>Tag Name</span>
                <span></span>
              </TableHeader>
              {tags.map((tag, index) => (
                <TableRow key={index}>
                  <span>{tag}</span>
                  <div className="actions">
                    <button className="edit">Edit</button>
                    <button className="delete">Delete</button>
                  </div>
                </TableRow>
              ))}
            </Table>

            <Footer>
              Showing 1 to 4 of 4 results
              <div>
                Per page: <select defaultValue="10">
                  <option>5</option>
                  <option>10</option>
                  <option>20</option>
                </select>
              </div>
            </Footer>
          </Screen>
        </IPadFrame>

        <Content>
          <h2>Simplify Your Subscriptions with Tags</h2>
          <p>
            Group subscriptions into categories or tags like Entertainment, Utilities, Tools, and more for better organization and quick access.
          </p>
          <p>
            Track spending by category and identify areas where you can optimize your costs.
          </p>
        </Content>
      </Container>
    </Section>
  );
};

export default TagsSection; 